/**
 * Finance settings — the transfer auto-match window + tolerance, and the
 * professional / reimbursement category pickers that the analytics
 * "professional recovery" report depends on. Reads GET /settings (the row is
 * created on first read) and saves partial changes via PATCH /settings.
 *
 * The category selects are fed from the live category tree, indented by depth
 * so the hierarchy reads at a glance.
 */

import { useMemo, useState } from 'react';
import {
  useFinanceSettings,
  usePatchFinanceSettings,
  useFinanceCategories,
  type FinanceSettingsInput,
  type FinanceCategoryDto,
} from '../../api/finance';
import styles from './finance.module.css';

interface CategoryOption {
  id: string;
  label: string;
}

export function FinanceSettingsScreen() {
  const settingsQ = useFinanceSettings();
  const categoriesQ = useFinanceCategories();
  const patch = usePatchFinanceSettings();

  const settings = settingsQ.data;

  // Editable mirror of the persisted settings. Initialized once data arrives.
  const [windowDays, setWindowDays] = useState('');
  const [tolerance, setTolerance] = useState('');
  const [professionalId, setProfessionalId] = useState<string>('');
  const [reimbursementId, setReimbursementId] = useState<string>('');
  const [loadedFor, setLoadedFor] = useState<string | null>(null);

  if (settings && loadedFor !== settings.id) {
    setLoadedFor(settings.id);
    setWindowDays(String(settings.transferWindowDays));
    setTolerance(String(settings.transferEurTolerancePct));
    setProfessionalId(settings.professionalCategoryId ?? '');
    setReimbursementId(settings.reimbursementCategoryId ?? '');
  }

  const options = useMemo(
    () => buildOptions(categoriesQ.data ?? []),
    [categoriesQ.data],
  );

  const windowNum = Number(windowDays);
  const tolNum = Number(tolerance);
  const windowValid = Number.isInteger(windowNum) && windowNum >= 1 && windowNum <= 14;
  const tolValid = Number.isFinite(tolNum) && tolNum >= 0 && tolNum <= 10;

  const dirty =
    !!settings &&
    (windowNum !== settings.transferWindowDays ||
      tolNum !== settings.transferEurTolerancePct ||
      (professionalId || null) !== settings.professionalCategoryId ||
      (reimbursementId || null) !== settings.reimbursementCategoryId);

  const canSave = !!settings && dirty && windowValid && tolValid && !patch.isPending;

  const save = () => {
    if (!canSave) return;
    const payload: FinanceSettingsInput = {
      transferWindowDays: windowNum,
      transferEurTolerancePct: tolNum,
      professionalCategoryId: professionalId || null,
      reimbursementCategoryId: reimbursementId || null,
    };
    patch.mutate(payload);
  };

  return (
    <div className={styles.page}>
      <header className={styles.head}>
        <div>
          <h2 className={styles.title}>Settings</h2>
          <div className={styles.subtitle}>
            Tune the deterministic transfer matcher and tell the analytics which
            categories track professional spend and its reimbursement.
          </div>
        </div>
      </header>

      {settingsQ.isLoading ? (
        <div className={styles.muted}>Loading…</div>
      ) : settingsQ.isError ? (
        <div className={styles.error}>Could not load finance settings.</div>
      ) : (
        <div className={styles.card}>
          <div className={styles.field}>
            <label className={styles.fieldLabel} htmlFor="set-window">
              Transfer match window (days)
            </label>
            <input
              id="set-window"
              className={styles.input}
              value={windowDays}
              inputMode="numeric"
              onChange={(e) => setWindowDays(e.target.value)}
            />
            <span className={styles.fieldHint}>
              Two opposite legs of equal EUR amount within this many days
              auto-pair. 1–14; default 4.
            </span>
            {!windowValid && (
              <span className={styles.fieldError}>
                Must be a whole number from 1 to 14.
              </span>
            )}
          </div>

          <div className={styles.field}>
            <label className={styles.fieldLabel} htmlFor="set-tol">
              EUR tolerance (%)
            </label>
            <input
              id="set-tol"
              className={styles.input}
              value={tolerance}
              inputMode="decimal"
              onChange={(e) => setTolerance(e.target.value)}
            />
            <span className={styles.fieldHint}>
              Used by the external Finance Advisor for fuzzy / cross-currency
              pairing — the platform matcher itself stays exact. 0–10.
            </span>
            {!tolValid && (
              <span className={styles.fieldError}>
                Must be a number from 0 to 10.
              </span>
            )}
          </div>

          <div className={styles.field}>
            <label className={styles.fieldLabel} htmlFor="set-prof">
              Professional category
            </label>
            <select
              id="set-prof"
              className={styles.select}
              value={professionalId}
              onChange={(e) => setProfessionalId(e.target.value)}
            >
              <option value="">— none —</option>
              {options.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.label}
                </option>
              ))}
            </select>
            <span className={styles.fieldHint}>
              Spend in this subtree counts toward professional outstanding.
            </span>
          </div>

          <div className={styles.field}>
            <label className={styles.fieldLabel} htmlFor="set-reimb">
              Reimbursement category
            </label>
            <select
              id="set-reimb"
              className={styles.select}
              value={reimbursementId}
              onChange={(e) => setReimbursementId(e.target.value)}
            >
              <option value="">— none —</option>
              {options.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.label}
                </option>
              ))}
            </select>
            <span className={styles.fieldHint}>
              Income in this subtree offsets professional spend.
            </span>
          </div>

          <div className={styles.saveBar}>
            <button
              type="button"
              className="kos-btn kos-btn-primary"
              disabled={!canSave}
              onClick={save}
            >
              {patch.isPending ? 'Saving…' : 'Save settings'}
            </button>
            {patch.isError && (
              <span className={styles.fieldError}>
                Save failed — try again.
              </span>
            )}
            {patch.isSuccess && !dirty && <span className={styles.savedHint}>Saved.</span>}
          </div>
        </div>
      )}
    </div>
  );
}

/** Flatten the category tree into indented options (parents before children). */
function buildOptions(flat: FinanceCategoryDto[]): CategoryOption[] {
  const childrenOf = new Map<string | null, FinanceCategoryDto[]>();
  for (const c of flat) {
    const key = c.parentId ?? null;
    const arr = childrenOf.get(key) ?? [];
    arr.push(c);
    childrenOf.set(key, arr);
  }
  for (const arr of childrenOf.values()) {
    arr.sort((a, b) => {
      if (a.parentId === null && a.kind !== b.kind) {
        return a.kind === 'income' ? -1 : 1;
      }
      return a.name.localeCompare(b.name);
    });
  }

  const out: CategoryOption[] = [];
  const walk = (parentId: string | null, depth: number) => {
    for (const c of childrenOf.get(parentId) ?? []) {
      out.push({ id: c.id, label: `${'  '.repeat(depth)}${c.name}` });
      walk(c.id, depth + 1);
    }
  };
  walk(null, 0);
  return out;
}
